<template>
    <div :class="['formAddress', mapAddress ? 'has-content' : '']">
        <template v-if="!isDisabled">
            <div class="columns">
                <div class="column">
                    <div
                        class="field _street"
                        :class="{
                            'has-error': v && v.street && v.street.$error,
                        }">
                        <label class="label">{{ $t('address.street') }}</label>

                        <template v-if="autoComplete">
                            <b-dropdown
                                aria-role="list"
                                :mobile-modal="false"
                                expanded
                                class="dropdown-addresses"
                                :class="{ hideDropdownMenu: !showStreetResults }"
                                :ref="`autoCompleteDropdown_${_uid}`">
                                <template #trigger>
                                    <div class="control is-clearfix">
                                        <input
                                            type="text"
                                            autocomplete="off"
                                            class="input"
                                            :value="editData.street"
                                            @input="(e) => updateAutoComplete(e)"
                                            :disabled="iniDisabled" />
                                    </div>
                                </template>
                                <template v-if="showStreetResults">
                                    <b-dropdown-item
                                        aria-role="listitem"
                                        v-for="(prediction, index) in streetResults"
                                        :key="'prediction-' + index"
                                        has-link>
                                        <a @click.prevent="getPlaceDetail(prediction)">
                                            {{ prediction.description }}
                                        </a>
                                    </b-dropdown-item>
                                </template>
                            </b-dropdown>
                        </template>
                        <template v-else>
                            <div class="control is-clearfix">
                                <input
                                    type="text"
                                    autocomplete="off"
                                    class="input"
                                    :value="editData.street"
                                    @input="(e) => updateField('street', e)"
                                    :disabled="iniDisabled" />
                            </div>
                        </template>
                    </div>
                </div>
                <div class="column is-4">
                    <div
                        class="field _number"
                        :class="{
                            'has-error': v && v.number && v.number.$error,
                        }">
                        <label class="label">{{ $t('address.houseNr') }}</label>
                        <div class="control is-clearfix">
                            <input
                                type="text"
                                autocomplete="off"
                                class="input"
                                :value="editData.number"
                                @input="(e) => updateField('number', e)"
                                :disabled="iniDisabled" />
                        </div>
                    </div>
                </div>
                <div class="column is-2">
                    <div
                        class="field _box"
                        :class="{
                            'has-error': v && v.box && v.box.$error,
                        }">
                        <label class="label">{{ $t('address.box') }}</label>
                        <div class="control is-clearfix">
                            <input
                                type="text"
                                autocomplete="off"
                                class="input"
                                :value="editData.box"
                                @input="(e) => updateField('box', e)"
                                :disabled="iniDisabled" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <div
                        class="field _postalcode"
                        :class="{
                            'has-error': v && v.postalCode && v.postalCode.$error,
                        }">
                        <label class="label">{{ $t('address.zip') }}</label>
                        <div class="control is-clearfix">
                            <input
                                type="text"
                                autocomplete="off"
                                class="input"
                                :value="editData.postalCode"
                                @input="(e) => updateField('postalCode', e)"
                                :disabled="iniDisabled" />
                        </div>
                    </div>
                </div>
                <div class="column">
                    <div
                        class="field _city"
                        :class="{
                            'has-error': v && v.city && v.city.$error,
                        }">
                        <label class="label">{{ $t('address.city') }}</label>
                        <div class="control is-clearfix">
                            <input
                                type="text"
                                autocomplete="off"
                                class="input"
                                :value="editData.city"
                                @input="(e) => updateField('city', e)"
                                :disabled="iniDisabled" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="columns">
                <div class="column is-half">
                    <div
                        class="field _country"
                        :class="{
                            'has-error': v && v.country && v.country.$error,
                        }">
                        <label class="label">{{ $t('address.country') }}</label>
                        <selectCountries v-model="editData.country" :isDisabled="isDisabled" @input="updateValue" />
                    </div>
                </div>
            </div>
            <mapView v-show="false" :id="'gmap' + _uid" :info="mapInfo" :address="mapAddress" height="250px" />
        </template>
        <template v-else-if="mapAddress">
            <template v-if="usePopover">
                <v-popover>
                    <a href="javascript:void(0)" class="txt contact">
                        <span class="icon">
                            <font-awesome-icon :icon="['fas', 'map-marker']" />
                        </span>
                        {{ mapAddress }}</a
                    >
                    <template slot="popover">
                        <mapView
                            :id="'gmap' + _uid"
                            :info="mapInfo"
                            :address="mapAddress"
                            height="250px"
                            width="500px" />
                    </template>
                </v-popover>
            </template>
            <template v-else>
                <b-collapse :open="!isCollapsed" v-if="mapAddress">
                    <template v-slot:trigger>
                        <a href="javascript:void(0)" class="txt contact">
                            <span class="icon">
                                <font-awesome-icon :icon="['fas', 'map-marker']" />
                            </span>
                            {{ mapAddress }}</a
                        >
                    </template>
                    <mapView :id="'gmap' + _uid" :info="mapInfo" :address="mapAddress" height="250px" />
                </b-collapse>
            </template>
        </template>
        <template v-else>
            <!--empty: show empty input-->
            <textInput value="" :iniDisabled="true" />
        </template>
    </div>
</template>

<script>
import { debounce } from './../mixins/helpers.js';
import selectCountries from './CountriesSelect.vue';
import aFormMixin from './../mixins/aForm.js';
const mapView = () => import('./MapGoogle.vue');

import Vue from 'vue';
import VTooltip from 'v-tooltip';
Vue.use(VTooltip);

import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faMapMarker } from '@fortawesome/free-solid-svg-icons';
library.add(faMapMarker);

import { googleAutoComplete, googlePlaceDetails } from './../services/google.js';

export default {
    name: 'AddressForm',
    components: {
        selectCountries,
        mapView,
        FontAwesomeIcon,
    },
    props: {
        v: {
            type: Object,
        },
        isCollapsed: {
            type: Boolean,
        },
        hideMap: {
            type: Boolean,
        },
        autoComplete: {
            type: Boolean,
            default: false,
        },
        value: {
            type: Object,
        },
        usePopover: { type: Boolean, default: false },
    },
    mixins: [aFormMixin],
    data() {
        return {
            streetResults: [],
            showStreetResults: false,
        };
    },
    watch: {
        value() {
            this.handleValue();
        },
    },
    computed: {
        countryName() {
            var name = '';
            if (this.editData?.country) {
                name = this.$t(`country.${this.editData.country.toUpperCase()}`);
            }
            return name;
        },
        showMap() {
            if ((!this.hideMap && this.editData.street) || (!this.hideMap && this.editData.country)) {
                return true;
            }
            return false;
        },

        mapAddress() {
            if (this.editData.street) {
                var txt = `${this.editData.street} ${this.editData.number}${this.editData.box}, ${this.editData.postalCode} ${this.editData.city} ${this.countryName}`;
                txt = txt.replace(new RegExp('null', 'g'), '');
                return txt.replace(new RegExp('undefined', 'g'), '');
            } else if (this.editData.country) {
                return this.editData.city ? `${this.editData.city} ${this.countryName}` : this.countryName;
            } else {
                return false;
            }
        },
        mapInfo() {
            return this.mapAddress;
        },
    },
    created() {
        this.handleValue();
    },
    methods: {
        handleValue() {
            if (this.value) {
                this.editData = JSON.parse(JSON.stringify(this.value));
            }
        },
        updateValue() {
            this.$emit('input', this.editData);
        },
        updateField(field, event) {
            this.editData[field] = event.target.value;
            this.$emit('input', this.editData);
        },
        updateAutoComplete: debounce(function (event) {
            var value = event.target.value;
            console.log('updateAutoComplete', value);

            //save street
            this.editData.street = value;

            //add city if defined
            if (this.editData.city) {
                value += ' ' + this.editData.city;
            }

            googleAutoComplete({ searchTerm: value, country: this.editData.country })
                .then((response) => {
                    if (response.length > 0) {
                        this.streetResults = response;
                        this.showStreetResults = true;

                        this.$nextTick(() => {
                            this.showStreetResults = true;
                        });
                    }
                })
                .catch((e) => {
                    console.log('error', e);
                    this.showStreetResults = false;
                    this.updateValue();
                });
        }, 500),
        getPlaceDetail(value) {
            if (value && value.place_id) {
                googlePlaceDetails({
                    placeId: value.place_id,
                    mapId: 'gmap' + this._uid,
                }).then((response) => {
                    var address = response;
                    this.$emit('placeSelected', address);

                    //assign values to editData
                    if (address?.street_number) {
                        this.editData.number = address.street_number;
                    }
                    if (address?.route) {
                        this.editData.street = address.route;
                    }
                    if (address?.postal_code) {
                        this.editData.postalCode = address.postal_code;
                    }
                    if (address?.locality) {
                        this.editData.city = address.locality;
                    }
                    if (address?.country) {
                        this.editData.country = address.country;
                    }
                    this.updateValue();
                });
            }
        },
    },
    i18n: {
        messages: {
            en: {
                ...require('./../i18n/countries.en.json'),
                address: {
                    city: 'City',
                    country: 'Country',
                    selectCountry: 'Select a country',
                    selectNationality: 'Select nationality',
                    street: 'Street',
                    houseNr: 'Number',
                    zip: 'ZIP code',
                    box: 'Box',
                },
            },
            fr: {
                ...require('./../i18n/countries.fr.json'),
                address: {
                    city: 'Ville',
                    country: 'Pays',
                    selectCountry: 'Choisissez un pays',
                    selectNationality: 'Choisissez une nationalité',
                    street: 'Rue',
                    houseNr: 'Numero',
                    zip: 'Code postal',
                    box: 'Box',
                },
            },
            nl: {
                ...require('./../i18n/countries.nl.json'),
                address: {
                    city: 'Stad',
                    country: 'Land',
                    selectCountry: 'Selecteer een land',
                    selectNationality: 'Selecteer een nationaliteit',
                    street: 'Straat',
                    houseNr: 'Nummer',
                    zip: 'Postcode',
                    box: 'Bus',
                },
            },
        },
    },
};
</script>

<style lang="scss">
.dropdown.dropdown-addresses .dropdown-menu .has-link a {
    white-space: wrap;
}
</style>
